import { Link } from "react-router-dom";
import Seo from "@/components/base/Seo";
import Breadcrumbs from "@/components/base/Breadcrumbs";
import { SITE } from "@/config/site";

const sections = [
  {
    id: "acceptance",
    title: "1. Acceptance of terms",
    body: [
      `These terms and conditions govern your use of ${SITE.name} and all related services, including browsing the directory, creating a listing and using the owner portal.`,
      "By accessing or using the website, you agree to be bound by these terms. If you do not agree, please do not use the service.",
    ],
  },
  {
    id: "listings",
    title: "2. Business listings",
    body: [
      "Owners are responsible for the accuracy of the information they submit, including business name, address, contact details, opening hours, categories and media.",
      "We reserve the right to review, edit, reject or remove any listing that is inaccurate, misleading, duplicated, unlawful or that violates these terms.",
      "A free listing is provided as-is. Paid plans add features such as verification, priority placement and analytics, as described on the Listing Plans page.",
    ],
  },
  {
    id: "verification",
    title: "3. Verification badges",
    body: [
      "A verified badge indicates that we have performed a reasonable check of a business's address and ownership details at the time of verification.",
      "Verification is not a guarantee of quality, safety or continued accuracy. Details can change and should be confirmed directly with the business.",
    ],
  },
  {
    id: "reviews",
    title: "4. Reviews and user content",
    body: [
      "You agree not to post content that is false, defamatory, abusive, discriminatory, spam or otherwise unlawful.",
      "We may moderate, remove or refuse to publish any content at our discretion. Business owners may respond to reviews but must not post fake reviews or manipulate ratings.",
    ],
  },
  {
    id: "payments",
    title: "5. Plans and payments",
    body: [
      "Paid plans are billed for the period selected at checkout. Invoices are available from your account.",
      "Unless required by law, fees for a period already started are non-refundable. You may change or cancel a plan from your account; changes take effect at the start of the next billing period unless agreed otherwise.",
    ],
  },
  {
    id: "use",
    title: "6. Acceptable use",
    body: [
      "You must not scrape, copy, resell or systematically extract the directory's data without written permission.",
      "You must not attempt to disrupt, overload or gain unauthorised access to the service or interfere with other users.",
    ],
  },
  {
    id: "liability",
    title: "7. Disclaimer and limitation of liability",
    body: [
      "The directory is provided on an 'as is' and 'as available' basis. We do not warrant that listings are complete, current or error-free.",
      "To the maximum extent permitted by law, we are not liable for any indirect or consequential loss arising from your use of the service or reliance on any listing.",
    ],
  },
  {
    id: "changes",
    title: "8. Changes to these terms",
    body: [
      "We may update these terms from time to time. Material changes will be reflected on this page with an updated revision date.",
      "Continued use of the service after changes take effect constitutes acceptance of the revised terms.",
    ],
  },
  {
    id: "law",
    title: "9. Governing law",
    body: [
      "These terms are governed by the laws of India, and any disputes are subject to the exclusive jurisdiction of the courts in Mysore, Karnataka.",
    ],
  },
];

export default function Terms() {
  return (
    <>
      <Seo
        title="Terms & Conditions"
        description={`Read the terms and conditions governing the use of ${SITE.name}, including business listings, reviews, payments and acceptable use.`}
        keywords="Mysore Yellow Pages terms, directory terms of service, listing terms"
        path="/terms"
      />

      <div className="border-b border-background-200 bg-background-100/70">
        <div className="mx-auto max-w-[1400px] px-4 md:px-6 py-8 md:py-10">
          <Breadcrumbs items={[{ label: "Terms & Conditions" }]} />
          <h1 className="mt-3 font-heading text-2xl md:text-4xl font-extrabold tracking-tight text-foreground-950">
            Terms &amp; conditions
          </h1>
          <p className="mt-2 max-w-2xl text-sm md:text-base text-foreground-600">
            The rules for using {SITE.name}, listing your business and interacting with our services.
          </p>
          <p className="mt-3 text-xs font-semibold text-foreground-500">Last updated: October 2026</p>
        </div>
      </div>

      <div className="mx-auto max-w-[1400px] px-4 md:px-6 py-10 md:py-14">
        <div className="grid grid-cols-1 lg:grid-cols-[260px_1fr] gap-6 lg:gap-10">
          <aside className="lg:sticky lg:top-24 h-fit">
            <nav className="rounded-lg border border-background-200 bg-background-50 p-4" aria-label="On this page">
              <h2 className="px-1 text-xs font-bold uppercase tracking-wide text-foreground-500">On this page</h2>
              <ul className="mt-3 space-y-1">
                {sections.map((s) => (
                  <li key={s.id}>
                    <a
                      href={`#${s.id}`}
                      className="block rounded-md px-2 py-1.5 text-sm text-foreground-700 hover:bg-background-100 hover:text-primary-700 cursor-pointer"
                    >
                      {s.title}
                    </a>
                  </li>
                ))}
              </ul>
            </nav>
          </aside>

          <article className="rounded-lg border border-background-200 bg-background-50 p-6 md:p-10">
            <div className="space-y-8">
              {sections.map((s) => (
                <section key={s.id} id={s.id} className="scroll-mt-24">
                  <h2 className="font-heading text-lg md:text-xl font-extrabold text-foreground-950">{s.title}</h2>
                  <div className="mt-3 space-y-3">
                    {s.body.map((p, i) => (
                      <p key={i} className="text-sm text-foreground-600 leading-relaxed">{p}</p>
                    ))}
                  </div>
                </section>
              ))}

              <section id="contact" className="scroll-mt-24 rounded-lg border border-primary-200 bg-primary-50 p-5">
                <h2 className="font-heading text-base font-bold text-foreground-950">Questions about these terms?</h2>
                <p className="mt-2 text-sm text-foreground-700">
                  Email us at <a href={`mailto:${SITE.email}`} className="font-semibold text-primary-800 break-all">{SITE.email}</a> or call{" "}
                  <a href={`tel:${SITE.phoneHref}`} className="font-semibold text-primary-800">{SITE.phone}</a>. You can also reach us via the{" "}
                  <Link to="/contact" className="font-semibold text-primary-800">contact page</Link>.
                </p>
                <p className="mt-3 text-xs text-foreground-500">{SITE.addressOneLine} · GSTIN: {SITE.gstin}</p>
              </section>
            </div>
          </article>
        </div>
      </div>
    </>
  );
}